import React, { useState, useEffect, useRef } from 'react';
import html2pdf from 'html2pdf.js';
import { 
  getRapportTresorerie, 
  getRapportRecouvrement, 
  getRapportPerformanceRecolteurs 
} from '../services/api';
import { notificationService } from '../utils/notifications';
import './RapportsFinanciers.css';

const RapportsFinanciers = () => {
  const [activeReport, setActiveReport] = useState('tresorerie');
  const [loading, setLoading] = useState(false);
  const [rapportData, setRapportData] = useState(null);
  
  // États pour les filtres
  const [filters, setFilters] = useState({
    periodeType: 'mois',
    dateDebut: '',
    dateFin: '',
    mois: new Date().getMonth() + 1,
    annee: new Date().getFullYear()
  });
  
  const printRef = useRef(null);

  useEffect(() => {
    loadRapport();
  }, [activeReport, filters]);

  const loadRapport = async () => {
    setLoading(true);
    setRapportData(null); // Réinitialiser les données avant le chargement
    try {
      let response;
      const params = {
        periodeType: filters.periodeType,
        ...(filters.periodeType === 'personnalise' 
          ? { dateDebut: filters.dateDebut, dateFin: filters.dateFin }
          : filters.periodeType === 'mois'
          ? { mois: filters.mois, annee: filters.annee }
          : { annee: filters.annee })
      };

      switch (activeReport) {
        case 'tresorerie':
          response = await getRapportTresorerie(params);
          break;
        case 'recouvrement':
          response = await getRapportRecouvrement(params);
          break;
        case 'performance':
          response = await getRapportPerformanceRecolteurs(params);
          break;
        default:
          return;
      }

      // Valider que la réponse contient des données
      if (response && response.data) {
        setRapportData(response.data);
      } else {
        console.error('Réponse invalide:', response);
        notificationService.error('Erreur', 'Les données reçues sont invalides');
        setRapportData(null);
      }
    } catch (error) {
      console.error('Erreur lors du chargement du rapport:', error);
      notificationService.error('Erreur', 'Impossible de charger le rapport');
      setRapportData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!printRef.current) return;

    try {
      const opt = {
        margin: [10, 10, 10, 10],
        filename: `Rapport_${activeReport}_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: printRef.current.scrollWidth,
          height: printRef.current.scrollHeight
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'landscape'
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      await html2pdf().set(opt).from(printRef.current).save();
      notificationService.success('PDF généré', 'Le rapport a été téléchargé avec succès.');
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      notificationService.error('Erreur', 'Erreur lors de la génération du PDF.');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'CDF',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      // Vérifier que la date est valide
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('fr-FR', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      });
    } catch (error) {
      console.error('Erreur de formatage de date:', error);
      return '-';
    }
  };

  return (
    <div className="rapports-financiers">
      <div className="rapports-header">
        <h2>📈 Rapports Financiers Avancés</h2>
        <div className="rapports-actions">
          <button className="btn btn-primary" onClick={handleDownloadPDF} disabled={!rapportData || loading}>
            📄 Télécharger PDF
          </button>
        </div>
      </div>

      {/* Onglets */}
      <div className="rapports-tabs">
        <button 
          className={`tab ${activeReport === 'tresorerie' ? 'active' : ''}`}
          onClick={() => setActiveReport('tresorerie')}
        >
          💰 Trésorerie
        </button>
        <button 
          className={`tab ${activeReport === 'recouvrement' ? 'active' : ''}`}
          onClick={() => setActiveReport('recouvrement')}
        >
          📊 Recouvrement
        </button>
        <button 
          className={`tab ${activeReport === 'performance' ? 'active' : ''}`}
          onClick={() => setActiveReport('performance')}
        >
          🏆 Performance Récolteurs
        </button>
      </div>

      {/* Filtres */}
      <div className="rapports-filters">
        <div className="filter-group">
          <label>Type de période</label>
          <select
            value={filters.periodeType}
            onChange={(e) => setFilters({ ...filters, periodeType: e.target.value })}
          >
            <option value="mois">Mois</option>
            <option value="annee">Année</option>
            <option value="personnalise">Personnalisée</option>
          </select>
        </div>

        {filters.periodeType === 'mois' && (
          <>
            <div className="filter-group">
              <label>Mois</label>
              <select
                value={filters.mois}
                onChange={(e) => setFilters({ ...filters, mois: parseInt(e.target.value) })}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (
                  <option key={m} value={m}>
                    {new Date(2000, m - 1, 1).toLocaleDateString('fr-FR', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Année</label>
              <input
                type="number"
                value={filters.annee}
                onChange={(e) => setFilters({ ...filters, annee: parseInt(e.target.value) })}
                min="2020"
                max={new Date().getFullYear() + 1}
              />
            </div>
          </>
        )}

        {filters.periodeType === 'annee' && (
          <div className="filter-group">
            <label>Année</label>
            <input
              type="number"
              value={filters.annee}
              onChange={(e) => setFilters({ ...filters, annee: parseInt(e.target.value) })}
              min="2020"
              max={new Date().getFullYear() + 1}
            />
          </div>
        )}

        {filters.periodeType === 'personnalise' && (
          <>
            <div className="filter-group">
              <label>Date début</label>
              <input
                type="date"
                value={filters.dateDebut}
                onChange={(e) => setFilters({ ...filters, dateDebut: e.target.value })}
              />
            </div>
            <div className="filter-group">
              <label>Date fin</label>
              <input
                type="date"
                value={filters.dateFin}
                onChange={(e) => setFilters({ ...filters, dateFin: e.target.value })}
              />
            </div>
          </>
        )}

        <button className="btn btn-secondary" onClick={loadRapport} disabled={loading}>
          {loading ? '⏳ Chargement...' : '🔄 Actualiser'}
        </button>
      </div>

      {/* Contenu du rapport */}
      <div className="rapports-content" ref={printRef}>
        {loading ? (
          <div className="loading">Chargement du rapport...</div>
        ) : rapportData ? (
          <>
            {activeReport === 'tresorerie' && <RapportTresorerie data={rapportData} formatCurrency={formatCurrency} formatDate={formatDate} />}
            {activeReport === 'recouvrement' && <RapportRecouvrement data={rapportData} formatCurrency={formatCurrency} formatDate={formatDate} />}
            {activeReport === 'performance' && <RapportPerformance data={rapportData} formatCurrency={formatCurrency} formatDate={formatDate} />}
          </>
        ) : (
          <div className="no-data">Aucune donnée disponible</div>
        )}
      </div>
    </div>
  );
};

// Composant pour le rapport de trésorerie
const RapportTresorerie = ({ data, formatCurrency, formatDate }) => {
  // Vérifications de sécurité
  if (!data || !data.revenus || !data.periode) {
    return (
      <div className="rapport-tresorerie">
        <div className="no-data">Chargement des données...</div>
      </div>
    );
  }

  const revenus = data.revenus || {};
  const projections = data.projections || {};
  const periode = data.periode || {};
  const details = data.details || [];
  const revenusParType = revenus.parType || {};

  return (
    <div className="rapport-tresorerie">
      <div className="rapport-header">
        <h3>Rapport de Trésorerie</h3>
        <p>Période: {formatDate(periode.dateDebut)} - {formatDate(periode.dateFin)}</p>
      </div>

      <div className="rapport-summary">
        <div className="summary-card">
          <h4>Revenus Total</h4>
          <p className="amount">{formatCurrency(revenus.total || 0)}</p>
          <p className="subtitle">{revenus.nombrePaiements || 0} paiement(s)</p>
        </div>
        <div className="summary-card">
          <h4>Projection Mois Suivant</h4>
          <p className="amount">{formatCurrency(projections.projectionMoisSuivant || 0)}</p>
        </div>
        <div className="summary-card">
          <h4>Projection Année</h4>
          <p className="amount">{formatCurrency(projections.projectionAnnee || 0)}</p>
        </div>
      </div>

      <div className="rapport-section">
        <h4>Revenus par Type</h4>
        {Object.keys(revenusParType).length > 0 ? (
          <table className="rapport-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Montant</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(revenusParType).map(([type, montant]) => (
                <tr key={type}>
                  <td>{type}</td>
                  <td>{formatCurrency(montant)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
            Aucun revenu par type disponible
          </p>
        )}
      </div>

      <div className="rapport-section">
        <h4>Détails des Paiements</h4>
        {details.length > 0 ? (
          <div className="table-container">
            <table className="rapport-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>N° Reçu</th>
                  <th>Membre</th>
                  <th>Type</th>
                  <th>Montant</th>
                </tr>
              </thead>
              <tbody>
                {details.map((paiement) => (
                  <tr key={paiement.id}>
                    <td>{formatDate(paiement.date)}</td>
                    <td>{paiement.numeroRecu || '-'}</td>
                    <td>{paiement.membre ? paiement.membre.nom : '-'}</td>
                    <td>{paiement.type || '-'}</td>
                    <td>{formatCurrency(paiement.montant || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
            Aucun paiement trouvé pour cette période
          </p>
        )}
      </div>
    </div>
  );
};

// Composant pour le rapport de recouvrement
const RapportRecouvrement = ({ data, formatCurrency, formatDate }) => {
  // Vérifications de sécurité
  if (!data || !data.recouvrement || !data.periode) {
    return (
      <div className="rapport-recouvrement">
        <div className="no-data">Chargement des données...</div>
      </div>
    );
  }

  const recouvrement = data.recouvrement || {};
  const periode = data.periode || {};
  const membresEnRetard = data.membresEnRetard || [];
  const montantsImpayesParAssociation = data.montantsImpayesParAssociation || {};

  return (
    <div className="rapport-recouvrement">
      <div className="rapport-header">
        <h3>Rapport de Recouvrement</h3>
        <p>Période: {periode.mois || '-'}/{periode.annee || '-'}</p>
      </div>

      <div className="rapport-summary">
        <div className="summary-card">
          <h4>Taux de Recouvrement</h4>
          <p className="amount">{(recouvrement.taux || 0).toFixed(2)}%</p>
        </div>
        <div className="summary-card">
          <h4>Montant Payé</h4>
          <p className="amount">{formatCurrency(recouvrement.montantPaye || 0)}</p>
        </div>
        <div className="summary-card">
          <h4>Montant Impayé</h4>
          <p className="amount error">{formatCurrency(recouvrement.montantImpaye || 0)}</p>
        </div>
        <div className="summary-card">
          <h4>Cotisations Impayées</h4>
          <p className="amount">{recouvrement.nombreCotisationsImpayees || 0}</p>
        </div>
      </div>

      {membresEnRetard && membresEnRetard.length > 0 && (
        <div className="rapport-section">
          <h4>Membres en Retard de Paiement</h4>
          <div className="table-container">
            <table className="rapport-table">
              <thead>
                <tr>
                  <th>Membre</th>
                  <th>Matricule</th>
                  <th>Association</th>
                  <th>Jours de Retard</th>
                  <th>Montant Dû</th>
                </tr>
              </thead>
              <tbody>
                {data.membresEnRetard.map((item, index) => (
                  <tr key={index}>
                    <td>{item.membre.nom}</td>
                    <td>{item.membre.matricule}</td>
                    <td>{item.membre.association}</td>
                    <td>{item.cotisation.joursRetard}</td>
                    <td>{formatCurrency(item.cotisation.montant)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="rapport-section">
        <h4>Montants Impayés par Association</h4>
        {Object.keys(montantsImpayesParAssociation).length > 0 ? (
          <table className="rapport-table">
            <thead>
              <tr>
                <th>Association</th>
                <th>Montant Impayé</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(montantsImpayesParAssociation).map(([assoc, montant]) => (
                <tr key={assoc}>
                  <td>{assoc}</td>
                  <td>{formatCurrency(montant)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
            Aucun montant impayé par association
          </p>
        )}
      </div>
    </div>
  );
};

// Composant pour le rapport de performance
const RapportPerformance = ({ data, formatCurrency, formatDate }) => {
  // Vérifications de sécurité
  if (!data || !data.periode || !data.totaux) {
    return (
      <div className="rapport-performance">
        <div className="no-data">Chargement des données...</div>
      </div>
    );
  }

  const periode = data.periode || {};
  const totaux = data.totaux || {};
  const classement = data.classement || [];

  return (
    <div className="rapport-performance">
      <div className="rapport-header">
        <h3>Rapport de Performance des Récolteurs</h3>
        <p>Période: {formatDate(periode.dateDebut)} - {formatDate(periode.dateFin)}</p>
      </div>

      <div className="rapport-summary">
        <div className="summary-card">
          <h4>Total Membres</h4>
          <p className="amount">{totaux.totalMembres || 0}</p>
        </div>
        <div className="summary-card">
          <h4>Total Récolté</h4>
          <p className="amount">{formatCurrency(totaux.totalMontant || 0)}</p>
        </div>
        <div className="summary-card">
          <h4>Total Paiements</h4>
          <p className="amount">{totaux.totalPaiements || 0}</p>
        </div>
        <div className="summary-card">
          <h4>Moyenne par Récolteur</h4>
          <p className="amount">{formatCurrency(totaux.moyenneMontant || 0)}</p>
        </div>
      </div>

      <div className="rapport-section">
        <h4>Classement des Récolteurs</h4>
        {classement.length > 0 ? (
          <div className="table-container">
            <table className="rapport-table">
              <thead>
                <tr>
                  <th>Rang</th>
                  <th>Récolteur</th>
                  <th>Matricule</th>
                  <th>Association</th>
                  <th>Nouveaux Membres</th>
                  <th>Montant Récolté</th>
                  <th>Paiements</th>
                  <th>Taux Conversion</th>
                </tr>
              </thead>
              <tbody>
                {classement.map((item) => (
                  <tr key={item.recolteur?.id || item.rang}>
                    <td>
                      <span className={`rank rank-${item.rang <= 3 ? 'top' : 'normal'}`}>
                        {item.rang}
                      </span>
                    </td>
                    <td>{item.recolteur?.nom || '-'}</td>
                    <td>{item.recolteur?.matricule || '-'}</td>
                    <td>{item.recolteur?.association || '-'}</td>
                    <td>{item.statistiques?.nouveauxMembres || 0}</td>
                    <td>{formatCurrency(item.statistiques?.montantRecolte || 0)}</td>
                    <td>{item.statistiques?.nombrePaiements || 0}</td>
                    <td>{(item.statistiques?.tauxConversion || 0).toFixed(2)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
            Aucun récolteur trouvé pour cette période
          </p>
        )}
      </div>
    </div>
  );
};

export default RapportsFinanciers;

